import React, { useState, useEffect } from 'react';
import { filesAPI } from '../services/api';
import { useNotification } from '../hooks/useNotification';
import ConfirmDialog from '../components/ConfirmDialog';
import {
  Folder,
  File,
  Upload,
  Download,
  Trash2,
  FolderPlus,
  Archive,
  ChevronRight,
  RefreshCw,
  FileArchive,
  Edit,
  Move
} from 'lucide-react';

const FileManager = () => {
  const { showSuccess, showError } = useNotification();
  const [currentPath, setCurrentPath] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDirModal, setShowCreateDirModal] = useState(false);
  const [newDirName, setNewDirName] = useState('');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showArchiveUploadModal, setShowArchiveUploadModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingFile, setEditingFile] = useState(null);
  const [fileContent, setFileContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [movingItem, setMovingItem] = useState(null);
  const [targetPath, setTargetPath] = useState('');
  const [moving, setMoving] = useState(false);

  useEffect(() => {
    loadFiles();
  }, [currentPath]);

  const loadFiles = async () => {
    try {
      setLoading(true);
      const response = await filesAPI.list(currentPath);
      setItems(response.data.items || []);
    } catch (err) {
      showError(err.response?.data?.message || 'Не удалось загрузить файлы');
    } finally {
      setLoading(false);
    }
  };

  const handleNavigate = (item) => {
    if (item.is_directory) {
      const newPath = currentPath ? `${currentPath}/${item.name}` : item.name;
      setCurrentPath(newPath);
    }
  };

  const handleCreateDirectory = async () => {
    if (!newDirName.trim()) {
      showError('Имя директории не может быть пустым');
      return;
    }

    try {
      const path = currentPath ? `${currentPath}/${newDirName.trim()}` : newDirName.trim();
      await filesAPI.createDirectory(path);
      showSuccess('Директория успешно создана');
      setShowCreateDirModal(false);
      setNewDirName('');
      loadFiles();
    } catch (err) {
      showError(err.response?.data?.message || 'Не удалось создать директорию');
    }
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;

    try {
      await filesAPI.delete(itemToDelete.path);
      showSuccess('Успешно удалено');
      setShowDeleteDialog(false);
      setItemToDelete(null);
      loadFiles();
    } catch (err) {
      showError(err.response?.data?.message || 'Не удалось удалить');
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setUploading(true);
      await filesAPI.upload(file, currentPath);
      showSuccess('Файл успешно загружен');
      setShowUploadModal(false);
      loadFiles();
    } catch (err) {
      showError(err.response?.data?.message || 'Не удалось загрузить файл');
    } finally {
      setUploading(false);
      event.target.value = ''; // Reset input
    }
  };

  const handleArchiveUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.zip')) {
      showError('Выберите ZIP-архив');
      return;
    }

    try {
      setUploading(true);
      await filesAPI.uploadArchive(file, currentPath);
      showSuccess('Архив успешно распакован');
      setShowArchiveUploadModal(false);
      loadFiles();
    } catch (err) {
      showError(err.response?.data?.message || 'Не удалось распаковать архив');
    } finally {
      setUploading(false);
      event.target.value = ''; // Reset input
    }
  };

  const handleDownload = async (item) => {
    try {
      const response = await filesAPI.download(item.path);
      
      // Create blob and download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', item.name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      showSuccess('Файл успешно скачан');
    } catch (err) {
      showError(err.response?.data?.message || 'Не удалось скачать файл');
    }
  };

  const handleDownloadArchive = async () => {
    try {
      const response = await filesAPI.downloadArchive(currentPath);
      
      // Create blob and download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const archiveName = currentPath ? currentPath.split('/').pop() || 'archive' : 'archive';
      link.setAttribute('download', `${archiveName}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      showSuccess('Архив успешно скачан');
    } catch (err) {
      showError(err.response?.data?.message || 'Не удалось создать архив');
    }
  };

  const getBreadcrumbs = () => {
    if (!currentPath) return [{ name: 'Root', path: '' }];
    
    const parts = currentPath.split('/').filter(Boolean);
    const breadcrumbs = [{ name: 'Root', path: '' }];
    
    parts.forEach((part, index) => {
      breadcrumbs.push({
        name: part,
        path: parts.slice(0, index + 1).join('/')
      });
    });
    
    return breadcrumbs;
  };

  const formatSize = (bytes) => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp * 1000).toLocaleString();
  };

  const isTextFile = (filename) => {
    const ext = filename.toLowerCase().split('.').pop();
    return ['yaml', 'yml', 'txt', 'ps1', 'cfg', 'md', 'jsp', 'php', 'py'].includes(ext);
  };

  const handleEditFile = async (item) => {
    if (!isTextFile(item.name)) {
      showError('Этот тип файла нельзя редактировать');
      return;
    }
    try {
      const response = await filesAPI.readFile(item.path);
      setEditingFile(item);
      setFileContent(response.data.content || '');
      setShowEditModal(true);
    } catch (err) {
      showError(err.response?.data?.message || 'Не удалось загрузить файл');
    }
  };

  const handleSaveFile = async () => {
    if (!editingFile) return;
    try {
      setSaving(true);
      await filesAPI.saveFile(editingFile.path, fileContent);
      showSuccess('Файл успешно сохранён');
      setShowEditModal(false);
      setEditingFile(null);
      setFileContent('');
      loadFiles();
    } catch (err) {
      showError(err.response?.data?.message || 'Не удалось сохранить файл');
    } finally {
      setSaving(false);
    }
  };

  const handleMoveFile = async () => {
    if (!movingItem || !targetPath.trim()) {
      showError('Нужно указать целевой путь');
      return;
    }
    try {
      setMoving(true);
      const fullTargetPath = currentPath ? `${currentPath}/${targetPath.trim()}` : targetPath.trim();
      await filesAPI.moveFile(movingItem.path, fullTargetPath);
      showSuccess('Файл успешно перемещён');
      setShowMoveModal(false);
      setMovingItem(null);
      setTargetPath('');
      loadFiles();
    } catch (err) {
      showError(err.response?.data?.message || 'Не удалось переместить файл');
    } finally {
      setMoving(false);
    }
  };

  return (
    <div style={{
      padding: '28px',
      backgroundColor: 'var(--bg-primary)',
      minHeight: '100vh',
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px',
      }}>
        <div className="page-header" style={{ marginBottom: 0 }}>
          <div className="page-header-icon">
            <Folder size={24} />
          </div>
          <div className="page-header-text">
            <h1>Файловый менеджер</h1>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={() => setShowCreateDirModal(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 16px',
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            <FolderPlus size={16} />
            Новая директория
          </button>
          <button
            onClick={() => setShowUploadModal(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 16px',
              backgroundColor: '#10b981',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            <Upload size={16} />
            Загрузить файл
          </button>
          <button
            onClick={() => setShowArchiveUploadModal(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 16px',
              backgroundColor: '#8b5cf6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            <Archive size={16} />
            Загрузить архив
          </button>
          {currentPath && (
            <button
              onClick={handleDownloadArchive}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 16px',
                backgroundColor: '#f59e0b',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              <FileArchive size={16} />
              Скачать архив
            </button>
          )}
          <button
            onClick={loadFiles}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 16px',
              backgroundColor: 'var(--bg-secondary)',
              color: 'var(--text-primary)',
              border: '1px solid var(--border-color)',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            <RefreshCw size={16} />
            Обновить
          </button>
        </div>
      </div>

      {/* Breadcrumbs */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '16px',
        padding: '12px',
        backgroundColor: 'var(--bg-secondary)',
        borderRadius: '6px',
        flexWrap: 'wrap',
      }}>
        {getBreadcrumbs().map((crumb, index) => (
          <React.Fragment key={index}>
            <button
              onClick={() => setCurrentPath(crumb.path)}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--text-primary)',
                cursor: 'pointer',
                fontSize: '14px',
                padding: '4px 8px',
                borderRadius: '4px',
              }}
              onMouseOver={(e) => {
                e.target.style.backgroundColor = 'var(--bg-tertiary)';
              }}
              onMouseOut={(e) => {
                e.target.style.backgroundColor = 'transparent';
              }}
            >
              {crumb.name}
            </button>
            {index < getBreadcrumbs().length - 1 && (
              <ChevronRight size={16} color="var(--text-secondary)" />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Files Table */}
      <div style={{
        backgroundColor: 'var(--bg-secondary)',
        borderRadius: '8px',
        border: '1px solid var(--border-color)',
        overflow: 'hidden',
      }}>
        {loading ? (
          <div style={{
            padding: '48px',
            textAlign: 'center',
            color: 'var(--text-secondary)',
          }}>
            Загрузка...
          </div>
        ) : items.length === 0 ? (
          <div style={{
            padding: '48px',
            textAlign: 'center',
            color: 'var(--text-secondary)',
          }}>
            Директория пуста
          </div>
        ) : (
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
          }}>
            <thead>
              <tr style={{
                backgroundColor: 'var(--bg-tertiary)',
                borderBottom: '1px solid var(--border-color)',
              }}>
                <th style={{
                  padding: '12px',
                  textAlign: 'left',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: 'var(--text-primary)',
                }}>Имя</th>
                <th style={{
                  padding: '12px',
                  textAlign: 'left',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: 'var(--text-primary)',
                }}>Размер</th>
                <th style={{
                  padding: '12px',
                  textAlign: 'left',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: 'var(--text-primary)',
                }}>Изменён</th>
                <th style={{
                  padding: '12px',
                  textAlign: 'right',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: 'var(--text-primary)',
                }}>Действия</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => (
                <tr
                  key={index}
                  style={{
                    borderBottom: '1px solid var(--border-color)',
                    cursor: item.is_directory ? 'pointer' : 'default',
                  }}
                  onMouseOver={(e) => {
                    if (item.is_directory) {
                      e.currentTarget.style.backgroundColor = 'var(--bg-tertiary)';
                    }
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                  onClick={() => handleNavigate(item)}
                >
                  <td style={{
                    padding: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}>
                    {item.is_directory ? (
                      <Folder size={20} color="#3b82f6" />
                    ) : (
                      <File size={20} color="var(--text-secondary)" />
                    )}
                    <span style={{
                      color: 'var(--text-primary)',
                      fontSize: '14px',
                    }}>
                      {item.name}
                    </span>
                  </td>
                  <td style={{
                    padding: '12px',
                    color: 'var(--text-secondary)',
                    fontSize: '14px',
                  }}>
                    {formatSize(item.size)}
                  </td>
                  <td style={{
                    padding: '12px',
                    color: 'var(--text-secondary)',
                    fontSize: '14px',
                  }}>
                    {formatDate(item.modified)}
                  </td>
                  <td style={{
                    padding: '12px',
                    textAlign: 'right',
                  }}>
                    <div style={{
                      display: 'flex',
                      gap: '8px',
                      justifyContent: 'flex-end',
                    }}>
                      {!item.is_directory && isTextFile(item.name) && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditFile(item);
                          }}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: 'transparent',
                            border: '1px solid var(--border-color)',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                          }}
                          title="Редактировать"
                        >
                          <Edit size={14} color="var(--text-primary)" />
                        </button>
                      )}
                      {!item.is_directory && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownload(item);
                          }}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: 'transparent',
                            border: '1px solid var(--border-color)',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                          }}
                          title="Скачать"
                        >
                          <Download size={14} color="var(--text-primary)" />
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setMovingItem(item);
                          setTargetPath(item.name);
                          setShowMoveModal(true);
                        }}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: 'transparent',
                          border: '1px solid var(--border-color)',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                        }}
                        title="Переместить"
                      >
                        <Move size={14} color="var(--text-primary)" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setItemToDelete(item);
                          setShowDeleteDialog(true);
                        }}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: 'transparent',
                          border: '1px solid var(--border-color)',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                        }}
                        title="Удалить"
                      >
                        <Trash2 size={14} color="#ef4444" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create Directory Modal */}
      {showCreateDirModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            backgroundColor: 'var(--bg-primary)',
            padding: '24px',
            borderRadius: '8px',
            width: '400px',
            border: '1px solid var(--border-color)',
          }}>
            <h2 style={{
              fontSize: '18px',
              fontWeight: 'bold',
              marginBottom: '16px',
              color: 'var(--text-primary)',
            }}>
              Создать директорию
            </h2>
            <input
              type="text"
              value={newDirName}
              onChange={(e) => setNewDirName(e.target.value)}
              placeholder="Имя директории"
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid var(--border-color)',
                borderRadius: '6px',
                fontSize: '14px',
                marginBottom: '16px',
                backgroundColor: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleCreateDirectory();
                }
              }}
              autoFocus
            />
            <div style={{
              display: 'flex',
              gap: '8px',
              justifyContent: 'flex-end',
            }}>
              <button
                onClick={() => {
                  setShowCreateDirModal(false);
                  setNewDirName('');
                }}
                style={{
                  padding: '8px 16px',
                  backgroundColor: 'var(--bg-secondary)',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '6px',
                  cursor: 'pointer',
                }}
              >
                Отмена
              </button>
              <button
                onClick={handleCreateDirectory}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                }}
              >
                Создать
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload File Modal */}
      {showUploadModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            backgroundColor: 'var(--bg-primary)',
            padding: '24px',
            borderRadius: '8px',
            width: '400px',
            border: '1px solid var(--border-color)',
          }}>
            <h2 style={{
              fontSize: '18px',
              fontWeight: 'bold',
              marginBottom: '16px',
              color: 'var(--text-primary)',
            }}>
              Загрузить файл
            </h2>
            <input
              type="file"
              onChange={handleFileUpload}
              disabled={uploading}
              style={{
                width: '100%',
                padding: '8px',
                marginBottom: '16px',
                fontSize: '14px',
              }}
            />
            {uploading && (
              <div style={{
                color: 'var(--text-secondary)',
                fontSize: '14px',
                marginBottom: '16px',
              }}>
                Загрузка...
              </div>
            )}
            <div style={{
              display: 'flex',
              gap: '8px',
              justifyContent: 'flex-end',
            }}>
              <button
                onClick={() => setShowUploadModal(false)}
                disabled={uploading}
                style={{
                  padding: '8px 16px',
                  backgroundColor: 'var(--bg-secondary)',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '6px',
                  cursor: uploading ? 'not-allowed' : 'pointer',
                  opacity: uploading ? 0.5 : 1,
                }}
              >
                Закрыть
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Archive Modal */}
      {showArchiveUploadModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            backgroundColor: 'var(--bg-primary)',
            padding: '24px',
            borderRadius: '8px',
            width: '400px',
            border: '1px solid var(--border-color)',
          }}>
            <h2 style={{
              fontSize: '18px',
              fontWeight: 'bold',
              marginBottom: '16px',
              color: 'var(--text-primary)',
            }}>
              Загрузить архив
            </h2>
            <p style={{
              fontSize: '14px',
              color: 'var(--text-secondary)',
              marginBottom: '16px',
            }}>
              Выберите ZIP-архив для распаковки
            </p>
            <input
              type="file"
              accept=".zip"
              onChange={handleArchiveUpload}
              disabled={uploading}
              style={{
                width: '100%',
                padding: '8px',
                marginBottom: '16px',
                fontSize: '14px',
              }}
            />
            {uploading && (
              <div style={{
                color: 'var(--text-secondary)',
                fontSize: '14px',
                marginBottom: '16px',
              }}>
                Распаковка...
              </div>
            )}
            <div style={{
              display: 'flex',
              gap: '8px',
              justifyContent: 'flex-end',
            }}>
              <button
                onClick={() => setShowArchiveUploadModal(false)}
                disabled={uploading}
                style={{
                  padding: '8px 16px',
                  backgroundColor: 'var(--bg-secondary)',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '6px',
                  cursor: uploading ? 'not-allowed' : 'pointer',
                  opacity: uploading ? 0.5 : 1,
                }}
              >
                Закрыть
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false);
          setItemToDelete(null);
        }}
        onConfirm={handleDelete}
        title="Удаление элемента"
        message={`Вы уверены, что хотите удалить "${itemToDelete?.name}"? Это действие необратимо.`}
        confirmText="Удалить"
        confirmColor="#ef4444"
      />

      {/* Edit File Modal */}
      {showEditModal && editingFile && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            backgroundColor: 'var(--bg-primary)',
            padding: '24px',
            borderRadius: '8px',
            width: '90vw',
            maxWidth: '800px',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            border: '1px solid var(--border-color)',
          }}>
            <h2 style={{
              fontSize: '18px',
              fontWeight: 'bold',
              marginBottom: '16px',
              color: 'var(--text-primary)',
            }}>
              Редактирование файла: {editingFile.name}
            </h2>
            <textarea
              value={fileContent}
              onChange={(e) => setFileContent(e.target.value)}
              style={{
                width: '100%',
                flex: 1,
                minHeight: '400px',
                padding: '12px',
                border: '1px solid var(--border-color)',
                borderRadius: '6px',
                fontSize: '14px',
                fontFamily: 'monospace',
                backgroundColor: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
                resize: 'vertical',
              }}
            />
            <div style={{
              display: 'flex',
              gap: '8px',
              justifyContent: 'flex-end',
              marginTop: '16px',
            }}>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingFile(null);
                  setFileContent('');
                }}
                disabled={saving}
                style={{
                  padding: '8px 16px',
                  backgroundColor: 'var(--bg-secondary)',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '6px',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  opacity: saving ? 0.5 : 1,
                }}
              >
                Отмена
              </button>
              <button
                onClick={handleSaveFile}
                disabled={saving}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  opacity: saving ? 0.5 : 1,
                }}
              >
                {saving ? 'Сохранение...' : 'Сохранить'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Move File Modal */}
      {showMoveModal && movingItem && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            backgroundColor: 'var(--bg-primary)',
            padding: '24px',
            borderRadius: '8px',
            width: '400px',
            border: '1px solid var(--border-color)',
          }}>
            <h2 style={{
              fontSize: '18px',
              fontWeight: 'bold',
              marginBottom: '16px',
              color: 'var(--text-primary)',
            }}>
              Перемещение {movingItem.is_directory ? 'директории' : 'файла'}: {movingItem.name}
            </h2>
            <div style={{
              fontSize: '14px',
              color: 'var(--text-secondary)',
              marginBottom: '8px',
            }}>
              Текущий путь: {movingItem.path}
            </div>
            <input
              type="text"
              value={targetPath}
              onChange={(e) => setTargetPath(e.target.value)}
              placeholder="Новое имя или путь"
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid var(--border-color)',
                borderRadius: '6px',
                fontSize: '14px',
                marginBottom: '16px',
                backgroundColor: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleMoveFile();
                }
              }}
              autoFocus
            />
            <div style={{
              display: 'flex',
              gap: '8px',
              justifyContent: 'flex-end',
            }}>
              <button
                onClick={() => {
                  setShowMoveModal(false);
                  setMovingItem(null);
                  setTargetPath('');
                }}
                disabled={moving}
                style={{
                  padding: '8px 16px',
                  backgroundColor: 'var(--bg-secondary)',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '6px',
                  cursor: moving ? 'not-allowed' : 'pointer',
                  opacity: moving ? 0.5 : 1,
                }}
              >
                Отмена
              </button>
              <button
                onClick={handleMoveFile}
                disabled={moving}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: moving ? 'not-allowed' : 'pointer',
                  opacity: moving ? 0.5 : 1,
                }}
              >
                {moving ? 'Перемещение...' : 'Переместить'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileManager;
